import { type FC, type ReactElement } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { UserRecommendationsRecommendationTile } from 'dibs-personalized/exports/UserRecommendationsGrid.ts';
import dibsCss from 'dibs-css';

import { HpSharedEditorialModule } from '../homepage/HpSharedEditorialModule/HpSharedEditorialModule.tsx';
import { MOBILE_ITEMS_PER_ROW } from '../homepage/HpSharedItemsGrid/constants.ts';
import {
    getHpPersonalizedMixedGridListingItemTrackingTrigger,
    getHpPersonalizedMixedGridVisibleEdges,
    MIXED_GRID_MOBILE_CAROUSEL_INDEX,
    MIXED_GRID_MOBILE_SLOT_COUNT,
} from './buildHpPersonalizedMixedGrid.ts';

import { type HpPersonalizedMixedGridMobile_viewer$key } from './__generated__/HpPersonalizedMixedGridMobile_viewer.graphql.ts';

import { getHpPersonalizedMixedGridShimmerItems } from './HpPersonalizedMixedGridShimmer.tsx';
import styles from './HpPersonalizedMixedGridMobile.module.css';

const viewerFragment = graphql`
    fragment HpPersonalizedMixedGridMobile_viewer on Viewer
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
        isPersonalizedContentAboveFoldTestVariant: { type: "Boolean!", defaultValue: false }
        mixedGridFirst: { type: "Int", defaultValue: 9 }
        mixedGridAfter: { type: "String" }
    ) {
        ...UserRecommendationsRecommendationTile_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
        homepageMixedGrid(
            homepageId: $homepageId
            userId: $userId
            userType: $userType
            recommendationType: $recommendationType
            abTestList: $abTestList
            isMobile: $isMobile
        ) @include(if: $isPersonalizedContentAboveFoldTestVariant) {
            editorialModule {
                ...HpSharedEditorialModule_componentModule
            }
            items(first: $mixedGridFirst, after: $mixedGridAfter)
                @connection(key: "HpPersonalizedMixedGrid_items") {
                edges {
                    node {
                        itemSource
                        item {
                            __typename
                            ... on Item {
                                serviceId
                                ...UserRecommendationsRecommendationTile_item
                                    @arguments(
                                        fetchFavorites: $hasUserId
                                        userId: $userId
                                        isTrade: $isTrade
                                        priceBookName: $priceBookName
                                    )
                            }
                        }
                    }
                }
            }
        }
        user(userId: $userId) @include(if: $hasUserId) {
            ...UserRecommendationsRecommendationTile_user
        }
    }
`;

type Props = {
    viewer: HpPersonalizedMixedGridMobile_viewer$key;
    afterDisplay: boolean;
    currency: string;
    includeAd: boolean;
    shimmerCount: number;
    loadMoreTracker: ReactElement | null;
};

export const HpPersonalizedMixedGridMobile: FC<Props> = ({
    viewer: viewerRef,
    afterDisplay,
    currency,
    includeAd,
    shimmerCount,
    loadMoreTracker,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const mixedGrid = viewer.homepageMixedGrid;
    const edges = getHpPersonalizedMixedGridVisibleEdges(
        (mixedGrid?.items?.edges || []).filter(filterFalsy),
        includeAd
    );
    const editorialModule = mixedGrid?.editorialModule;

    let productIndex = 0;
    const renderedEdges = edges
        .map(edge => {
            const item = edge.node?.item;
            if (item?.__typename !== 'Item') {
                return null;
            }

            const currentProductIndex = productIndex++;
            return (
                <UserRecommendationsRecommendationTile
                    key={item.serviceId || currentProductIndex}
                    item={item}
                    viewer={viewer}
                    user={viewer.user}
                    index={currentProductIndex}
                    itemsPerRow={MOBILE_ITEMS_PER_ROW}
                    isMobile
                    trackingTrigger={getHpPersonalizedMixedGridListingItemTrackingTrigger(
                        edge.node?.itemSource
                    )}
                    dataTn="hp-personalized-mixed-grid"
                    currency={currency}
                    afterDisplay={afterDisplay}
                />
            );
        })
        .filter(filterFalsy)
        .slice(0, MIXED_GRID_MOBILE_SLOT_COUNT);
    const renderedTiles = [
        ...renderedEdges,
        ...getHpPersonalizedMixedGridShimmerItems({
            count: shimmerCount,
            startIndex: renderedEdges.length,
            isMobile: true,
            loadMoreTracker,
        }),
    ];

    const beforeCarousel = renderedTiles.slice(0, MIXED_GRID_MOBILE_CAROUSEL_INDEX);
    const afterCarousel = renderedTiles.slice(MIXED_GRID_MOBILE_CAROUSEL_INDEX);

    return (
        <>
            <div className={classnames(styles.mobileGrid, dibsCss.relative)}>{beforeCarousel}</div>
            {editorialModule ? (
                <HpSharedEditorialModule
                    componentModule={editorialModule}
                    isMobile
                    editorialModuleOrder={1}
                />
            ) : null}
            <div className={classnames(styles.mobileGrid, dibsCss.relative)}>{afterCarousel}</div>
        </>
    );
};
